import { Hidden, useMediaQuery } from '@mui/material';
import { Author, AuthorImage, asCompanyAuthor } from 'domain/shared/author';
import useDialogHandler from 'hooks/useDialogHandler';
import React, { lazy, useState, useEffect } from 'react';
import { useLocation, useHistory, Prompt } from 'react-router-dom';
import { AllCategories } from 'types/content';
import { CompanyUpdateVisibility } from 'types/content/update';
import Button from 'ui/elements/buttons/Button';
import { DocumentList } from 'types/documents';
import Loading from 'ui/elements/Loading';

import Settings from './Settings';
import Dropzone from 'domain/shared/Files/Upload/Dropzone';
import Dialog, { Title, Content } from 'ui/views/dialogs/Dialog';
import { bluePlanetTheme } from 'ui/theme';
import BulbIcon from 'ui/elements/icons/BulbIcon';
import {
  communityMentionSearchFunction,
  companyUserMentionSearchFunction,
  removeMentions,
} from 'ui/elements/form/RichTextEditor/utils';
import Label from 'ui/elements/form/Label';
import { useUpdateForm } from 'domain/Updates/shared/UpdateForm/useUpdateForm';
import { companyUpdateAPI } from 'apis/CompanyAPI/content/UpdateAPI';
import i18n from 'util/i18n';
import { useCommunityMemberships } from 'apis/CompanyAPI/companies/useCommunityMemberships';
import { ICompany } from 'types/company';
import AttachmentsCarousel from 'domain/Updates/shared/UpdateForm/AttachmentsCarousel';
import { CommunityTopicListWrapper } from 'domain/Updates/community/CommunityTopicList';
import { getVisibilityLabel } from 'domain/Updates/shared/Visibility/utils';
import VisibilityButton from 'domain/Updates/shared/Visibility/VisibilityButton';
import UpdateIdeas, { getHelpOptions } from './UpdateIdeas';
import { CompanyUpdateFormValues } from './types';
import { PostTo, PostToCommunity, PostToCompany } from '../shared/types';
import { VisibilitySelector } from '../shared/Visibility/VisibilitySelector';
import { getOrUndefined } from 'util/resource';
import useResource from 'util/resource/useResource';
import kpiAPI from 'apis/KpiAPI';
import { KpiWithDisplay, KpiEmbed } from 'domain/kpis/types';
import { kpiAsEmbed } from 'domain/kpis/utils';
import { companiesApi } from 'apis/CompanyAPI/companies/companiesApi';

const RichTextEditor = lazy(() => import('ui/elements/form/RichTextEditor'));

interface Props {
  titleDraft?: string;
  contentDraft?: string;
  category?: AllCategories;
  bannerImage?: string;
  company: ICompany;
  onSave: (dto: CompanyUpdateFormValues, postTo: PostToCompany | PostToCommunity) => void;
  isSaving: boolean;
  author: Author;
  onClose: () => void;
  selectedVisibility: CompanyUpdateVisibility;
  attachments?: DocumentList;
  linkPreview?: string;
  saveButtonText?: string;
  contentId?: string;
  initialCursorPosition?: number;
}

export default function UpdateForm(props: Props) {
  const { company, contentId } = props;
  const location = useLocation();
  const history = useHistory();
  const [isDialogMounted, setIsDialogMounted] = useState(false);

  // Track that dialog has been mounted to prevent prompt on initial render
  useEffect(() => {
    setIsDialogMounted(true);
  }, []);

  const [showHelpSection, setShowHelpSection] = useState(false);
  const [communityAudience, setCommunityAudience] = useState<number>();
  const [pendingKpiEmbed, setPendingKpiEmbed] = useState<KpiEmbed | undefined>(undefined);

  const getImageUploadUrl = (filename: string) => companiesApi.images.getCompanyUploadUrl(props.company.id, filename);
  const api = companyUpdateAPI(props.company.id);

  const settingsDialogHandler = useDialogHandler(false);
  const [isVisiblitiySelectorOpen, setIsVisibilitySelectorOpen] = useState(false);
  const [postUpdateTo, setPostUpdateTo] = useState<PostToCompany | PostToCommunity>({
    type: 'company',
    visibility: props.selectedVisibility,
    profile: props.company,
  });

  const { formik, onCancel, onSubmit, onUploadFile, onAttachmentRemoved, newAttachments, removeFile, hasRealChanges } = useUpdateForm({
    ...props,
    onSave: dto => props.onSave(dto, postUpdateTo),
  });

  const isMobile = useMediaQuery(bluePlanetTheme.breakpoints.down('sm'));

  // When posting to community, show company logo instead of user profile image
  const displayAuthor = postUpdateTo.type === 'community' ? asCompanyAuthor(undefined, props.company) : props.author;

  // Fetch KPIs for embedding
  const { resource: kpisResource } = useResource<KpiWithDisplay[]>(kpiAPI.kpi.list(props.company.id));

  // Check for pending KPI embed from URL params (from "Share in update" flow)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const embedKpiId = params.get('embedKpi');
    const kpiVisibility = params.get('kpiVisibility') as 'Public' | 'Private' | null;

    // Only process if we haven't already set a pending KPI embed
    if (embedKpiId && kpisResource.state === 'fetched' && !pendingKpiEmbed) {
      // Find the KPI from fetched data
      const kpi = kpisResource.resource.find((k: KpiWithDisplay) => k.details.id === embedKpiId);

      if (kpi) {
        setPendingKpiEmbed(kpiAsEmbed(kpi, props.company));

        // Set the post visibility to match the KPI's visibility (only for company posts)
        if (kpiVisibility && postUpdateTo.type === 'company') {
          setPostUpdateTo({
            ...postUpdateTo,
            visibility: kpiVisibility,
          });
        }

        // Clear the URL params to prevent re-adding the KPI
        params.delete('embedKpi');
        params.delete('kpiVisibility');
        const newSearch = params.toString();
        history.replace({
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        });
      }
    }
  }, [location.search, kpisResource.state]);

  const { resource: communityMembershipsResource } = useCommunityMemberships(props.company.slug);

  const mentionSearchFunction = async (searchQuery: string) =>
    postUpdateTo.type === 'company'
      ? companyUserMentionSearchFunction(searchQuery, postUpdateTo.visibility, props.company.id)
      : postUpdateTo.type === 'community'
        ? communityMentionSearchFunction(searchQuery, 'CommunityMembers', postUpdateTo.profile.id)
        : [];

  const onClose = () => onCancel(props.onClose);
  const maxWidth = settingsDialogHandler.isOpen || isVisiblitiySelectorOpen ? undefined : 'md';

  // The owners information category is still a hack. If should only be allowed to be used for private updates
  // if we change visibility we have to reset the category.

  const onSetPostUpdateTo = (postUpdateTo: PostToCompany | PostToCommunity) => {
    if (postUpdateTo.type !== 'company' || postUpdateTo.visibility !== 'Private') {
      formik.setFieldValue('category', 'News');
    }
    setPostUpdateTo(postUpdateTo);
    setCommunityAudience(postUpdateTo.profile.id);

    // when we change visibility, we don't know if the mentioned companies/users can still see the update, so we remove them
    formik.setFieldValue('structuredContent', removeMentions(formik.values.structuredContent));
  };

  const canSelectCommunities = contentId === undefined;
  const communityMemberships = getOrUndefined(communityMembershipsResource)?.values ?? [];
  const communitiesICanPostTo = canSelectCommunities
    ? communityMemberships.filter(
        community =>
          community.communityVisibility === 'Visible' && community.community.communityType === 'investor_arena',
      )
    : [];

  const options: (PostTo | string)[] = [
    { type: 'company', visibility: 'Public', profile: company },
    { type: 'company', visibility: 'Private', profile: company },
    ...(communitiesICanPostTo.length > 0 ? ['Communities you are part of:'] : []),
    ...communitiesICanPostTo.map(
      c =>
        ({
          type: 'community',
          profile: c.community,
          visibility: 'CommunityMembers',
        }) as const,
    ),
  ];

  return (
    <>
      <Prompt when={isDialogMounted && hasRealChanges && !formik.isSubmitting} message={i18n('en').common.unsavedChangesPrompt} />
      <Dialog maxWidth={maxWidth} fullScreen={isMobile} marginTop="15vh" open onClose={onClose}>
        {settingsDialogHandler.isOpen ? (
        <>
          <Title onClose={settingsDialogHandler.close}>Advanced Settings</Title>
          <Settings
            company={props.company}
            onClose={settingsDialogHandler.close}
            values={formik.values}
            setFieldValue={formik.setFieldValue}
            setPostUpdateTo={onSetPostUpdateTo}
          />
        </>
      ) : isVisiblitiySelectorOpen ? (
        <VisibilitySelector
          value={postUpdateTo}
          onChange={onSetPostUpdateTo}
          options={options}
          onClose={() => setIsVisibilitySelectorOpen(false)}
          onExtraAction={settingsDialogHandler.open}
        />
      ) : (
        <>
          <Title onClose={onClose}>
            <div className="u-flex u-flex-space-between u-flex-align-center" style={{ width: '100%' }}>
              <div className="u-flex u-flex-align-center">
                <AuthorImage className="u-half-spacing-right" author={displayAuthor} />
                <VisibilityButton
                  label={getVisibilityLabel(postUpdateTo)}
                  onClick={() => setIsVisibilitySelectorOpen(true)}
                />
              </div>
              <Hidden smDown>
                <Button
                  onClick={() => onSubmit()}
                  kind="primary"
                  type="submit"
                  data-intercom-target="publish"
                  isLoading={props.isSaving}
                  className="u-half-spacing-right"
                >
                  {props.saveButtonText || 'Publish'}
                </Button>
              </Hidden>
            </div>
          </Title>
          <Content className="u-section-spacing-bottom">
            {communityAudience && !props.contentId && postUpdateTo.type === 'community' && (
              <CommunityTopicListWrapper
                communityId={communityAudience}
                onChange={topicId => formik.setFieldValue('topicId', topicId)}
              />
            )}
            {!isMobile && (
              <div className="u-section-spacing-bottom">
                <Button
                  kind="tertiary"
                  onClick={() => setShowHelpSection(prev => !prev)}
                  className="u-flex u-flex-align-center"
                >
                  <BulbIcon color="blue" className="u-half-spacing-right" />
                  <Label style={{ margin: 0 }}>Post ideas</Label>
                </Button>
                {showHelpSection && <UpdateIdeas helpOptions={getHelpOptions().filter(opt => opt.value !== 'greet')} />}
              </div>
            )}

            <React.Suspense fallback={<Loading spacing="content-spacing" position="center" />}>
              <Dropzone disableOnClick onDrop={onUploadFile} tabIndex={-1}>
                <RichTextEditor
                  toolbarType="none"
                  name="content"
                  deltaName="structuredContent"
                  content={formik.values.content}
                  delta={formik.values.structuredContent || {}}
                  linkPreview={props.linkPreview}
                  setFieldValue={formik.setFieldValue}
                  setFieldTouched={formik.setFieldTouched}
                  validateField={formik.validateField}
                  handleChange={formik.handleChange}
                  error={formik.touched.content ? formik.errors.content : undefined}
                  getImageUploadUrl={getImageUploadUrl}
                  mentionSearchFunction={mentionSearchFunction}
                  onUploadFile={onUploadFile}
                  kpiOptions={{
                    company: props.company,
                    pendingKpiEmbed,
                    onKpiEmbedInserted: () => setPendingKpiEmbed(undefined),
                  }}
                  submitButton={
                    <Button
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={() => onSubmit()}
                      kind="primary"
                      type="submit"
                      data-intercom-target="publish"
                      isLoading={props.isSaving}
                    >
                      {props.saveButtonText || 'Publish'}
                    </Button>
                  }
                  attachmentSection={
                    <AttachmentsCarousel
                      attachmentIdList={formik.values.attachments}
                      existingAttachments={props.attachments?.documents ?? []}
                      newAttachments={newAttachments}
                      downloadAttachment={documentId => api.attachments.download(props.contentId || '', documentId)}
                      removeExistingAttachment={onAttachmentRemoved}
                      removeNewAttachment={removeFile}
                    />
                  }
                  autoFocus
                  initialCursorPosition={props.initialCursorPosition}
                />
              </Dropzone>
            </React.Suspense>
          </Content>
        </>
      )}
      </Dialog>
    </>
  );
}
